import { STORE_CONFIG, CONFIG_MODES } from '@/utils/constants/drs/store-config'
import { FRACTIONS } from '@/utils/constants/drs/fractions'

const mapConfiguration = (rootGetters, data, locationId) => {
    const setConfiguration = (options, activeMode) =>
        options.map(el => ({ ...el, isActive: el.mode === activeMode }))

    const locationName = rootGetters['drsStores/getFilterLocationData']?.full_name

    const fractions = data.fractions.map(({ id, dict_operator_id, is_active }) => ({
        ...Object.values(FRACTIONS).find(f => f.id === id),
        is_active,
        dict_operator_id,
        operatorName: rootGetters['drsOperators/getServiceByOperatorId'](dict_operator_id)?.name
    }))

    const keys = Object.values(CONFIG_MODES)
    const configModes = Object.fromEntries(
        keys.map(key => [key, setConfiguration(STORE_CONFIG[key], data[key])])
    )

    return { configModes, fractions, locationId, locationName }
}

const state = () => ({
    configuration: null,
    automaticConfigurations: []
})

const mutations = {
    SET_CONFIGURATION(state, payload) {
        state.configuration = payload
    },
    SET_AUTOMATIC_CONFIGURATIONS(state, payload) {
        state.automaticConfigurations = payload
    }
}

const actions = {
    async fetchAndMapConfiguration({ commit, state, rootGetters }) {
        try {
            commit('SET_CONFIGURATION', null)

            // get selected locationId from filters
            const locationId = rootGetters['drsStores/getFilters']?.locationId?.value
            if (!locationId) return

            const { data, status } = await this.$api.get(
                `drs/api/proxy/entity/location/configuration/${locationId}`,
                { config: { noCancel: true } }
            )
            if (status !== 200) {
                throw new Error('Failed fetchAndMapConfiguration')
            }
            commit('SET_CONFIGURATION', mapConfiguration(rootGetters, data, locationId))
        } catch (error) {
            console.error(error)
        }
    },

    async saveConfiguration({ dispatch, commit, rootGetters }, { locationId, payload, showNotification = true }) {
        try {
            const { status } = await this.$api.put(
                `drs/api/proxy/entity/location/configuration/${locationId}`,
                payload
            )
            if (status !== 200) {
                throw new Error('Failed saveConfiguration')
            }
            if (showNotification) {
                dispatch('addNotification', {
                    type: 'success',
                    content: 'Zmiany zapisano'
                }, { root: true })
            }
            commit('SET_CONFIGURATION', mapConfiguration(rootGetters, payload, locationId))
        } catch (error) {
            console.error(error)
            if (showNotification) {
                dispatch('addNotification', {
                    type: 'danger',
                    content: 'Wystąpił błąd'
                }, { root: true })
            }
        }
    },

    async fetchAutomaticConfigurations({ commit }, rvmDevices) {
        if (!rvmDevices?.length) return

        commit('SET_AUTOMATIC_CONFIGURATIONS', [])

        const configurations = await Promise.all(rvmDevices.map(async device => {
            try {
                const { data, status } = await this.$api.get(
                    'drs/api/proxy/users/[drs_user_id]/device/configuration',
                    {
                        params: { mcuid: device.rad_id },
                        config: { noCancel: true }
                    }
                )

                if (status !== 200) {
                    throw new Error('Failed fetchAutomaticConfiguration')
                }

                return {
                    device,
                    mcuid: device.rad_id,
                    package_fraction_mode: data.package_fraction_mode,
                    operator: data.operator
                }
            } catch (error) {
                console.error(error)
                return false
            }
        }))

        commit('SET_AUTOMATIC_CONFIGURATIONS', configurations.filter(configuration => !!configuration))
    },

    async saveAutomaticConfiguration({ state, commit, dispatch }, payload) {
        try {
            const { status } = await this.$api.patch(
                'drs/api/proxy/users/[drs_user_id]/device/configuration',
                payload
            )

            if (status !== 200) {
                throw new Error('Failed saveAutomaticConfiguration')
            }

            commit('SET_AUTOMATIC_CONFIGURATIONS', state.automaticConfigurations.map(configuration => {
                if (configuration.mcuid !== payload.mcuid) return configuration

                return { ...configuration, package_fraction_mode: payload.package_fraction_mode }
            }))
            dispatch('addNotification', {
                type: 'success',
                content: 'Zmiany zapisano'
            }, { root: true })
        } catch (error) {
            console.error(error)
            dispatch('addNotification', {
                type: 'danger',
                content: 'Wystąpił błąd'
            }, { root: true })
        }
    }
}

const getters = {
    configuration: state => state.configuration,
    automaticConfigurations: state => state.automaticConfigurations
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
